import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '@scandium-oy/ngx-scandium';
import { addHours, format, startOfDay } from 'date-fns';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { SelectDayComponent } from 'src/app/components/select-day/select-day.component';
import { SelectedUser } from 'src/app/components/select-user/select-user.component';
import { SelectProjectPickerComponent } from 'src/app/components2/select-project-picker/select-project-picker.component';
import { SelectSitePickerComponent } from 'src/app/components2/select-site-picker/select-site-picker.component';
import { SelectUserPickerComponent } from 'src/app/components2/select-user-picker/select-user-picker.component';
import { Options } from 'src/app/models/options.model';
import { SiteProject } from 'src/app/models/site-project.model';
import { Site } from 'src/app/models/site.model';
import { Timesheet, TimesheetPaymentType } from 'src/app/models/timesheet.model';
import { User } from 'src/app/models/user.model';
import { absenceSite, getAbsences } from 'src/app/report/helper';
import { ContractorsService } from 'src/app/services/contractors.service';
import { SiteProjectsService } from 'src/app/services/site-projects.service';
import { SitesService } from 'src/app/services/sites.service';
import { UsersService } from 'src/app/services/users.service';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';
import { storageKeys } from 'src/app/utility/constants';
import { Roles } from 'src/app/utility/role';
import { fieldSorter } from 'src/app/utility/sort';
import { capitalizeFirstLetter } from 'src/app/utility/string';

const workdayStart = 7;
const workdayEnd = 15;
const workdayEndMinutes = 30;

type EntryType = 'absence' | 'work' | null;

@Component({
  selector: 'app-new-timesheet-dialog',
  templateUrl: './new-timesheet.dialog.html',
  styleUrls: ['./new-timesheet.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AppCommonModule,
    SelectProjectPickerComponent,
    SelectSitePickerComponent,
    SelectUserPickerComponent,
    SelectDayComponent,
    ModalShellComponent,
  ],
})
export class NewTimesheetDialogComponent implements OnInit {
  private contractorsService = inject(ContractorsService);
  private formBuilder = inject(FormBuilder);
  private modalCtrl = inject(ModalController);
  private sitesService = inject(SitesService);
  private siteProjectsService = inject(SiteProjectsService);
  private storageService = inject(StorageService);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);

  private absences = getAbsences(this.translate);
  private user$: ReplaySubject<SelectedUser> = new ReplaySubject(1);
  site$ = new BehaviorSubject<Site>(null);

  formGroup: FormGroup;
  preUser = false;
  milageAllowance = 0;
  milageAllowanceSummary = '0.00';
  entryTypeValue = signal<EntryType>(null);
  selectedSiteForPicker = signal<Site>(null);
  selectedProjectForPicker = signal<SiteProject>(null);
  readonly isManagerWorkerRole = computed(() => this.usersService.role() === Roles.managerWorker);

  site = input<Site>();
  user = input<SelectedUser>();
  date = input<Date>(new Date());
  projectGuid = input<string>(null);
  canChangeSite = input(false);
  split = input(false);

  selectedUser$ = this.user$.pipe(
    map((user) => ({ guid: user.guid, name: user.name ?? user.displayName, displayName: user.displayName }) as SelectedUser),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  sites$: Observable<Site[]> = combineLatest([this.user$, this.usersService.role$]).pipe(
    filter(([user]) => user != null),
    switchMap(([user, role]) => {
      if (role === Roles.admin) {
        return this.sitesService.getActiveList();
      } else if (user.worksites?.length > 0) {
        return this.sitesService.getByGuids(user.worksites);
      }
      return of([]);
    }),
    tap((sites) => {
      if (sites.length === 1) {
        setTimeout(() => {
          this.onSiteSelected(sites[0]);
        }, 0);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  users$ = combineLatest([this.site$, this.contractorsService.getCurrentContractor()]).pipe(
    switchMap(([site, contractor]) => this.usersService.getListOnce({
      site: site?.guid ?? null,
      roles: [Roles.worker, Roles.supervisor, Roles.admin],
      contractor: contractor.guid,
    })),
    map((users) => users.map((user) => {
      user.displayName = capitalizeFirstLetter(user.displayName);
      return user;
    }).sort(fieldSorter(['displayName']))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  projects$ = combineLatest([this.site$, this.contractorsService.getCurrentContractor()]).pipe(
    filter(([site, contractor]) => site != null && contractor != null),
    switchMap(([site, contractor]) => this.siteProjectsService.getList({ site: site.guid, contractor: contractor.guid })),
    tap((projects) => {
      const currentProjectGuid = this.formGroup.get('project').value as string | null;
      const targetProjectGuid = currentProjectGuid ?? this.projectGuid();
      if (targetProjectGuid) {
        const preselectedProject = projects.find((project) => project.guid === targetProjectGuid);
        if (preselectedProject) {
          this.setProject(preselectedProject);
          return;
        }
      }
      if (projects?.length === 1 && this.formGroup.get('project').value == null) {
        this.setProject(projects[0]);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  litteras$ = this.site$.pipe(
    map((site) => site?.litteras ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  ngOnInit(): void {
    const selectedDate = this.date() ?? new Date();
    const startDate = addHours(startOfDay(selectedDate), workdayStart);
    const endDate = addHours(startOfDay(selectedDate), workdayEnd);
    endDate.setMinutes(workdayEndMinutes);

    if (this.user() != null) {
      this.preUser = true;
      this.user$.next(this.user());
    }

    this.site$.next(this.site() ?? null);
    this.selectedSiteForPicker.set(this.site() ?? null);

    this.formGroup = this.formBuilder.group({
      entryType: [null, Validators.required],
      site: [this.site()?.guid ?? null],
      siteName: [this.site()?.name ?? null],
      user: [this.user()?.guid ?? '', Validators.required],
      username: [this.user()?.displayName ?? ''],
      dateIn: [this.toDateTimeLocal(startDate), Validators.required],
      dateOut: [this.toDateTimeLocal(endDate), Validators.required],
      checked: [false],
      contractor: [this.contractorsService.contractorS()?.guid],
      project: [this.projectGuid() ?? null],
      projectName: [null],
      absence: [null],
      littera: [null],
      kmPayUnit: [''],
      fullDay: [false],
      partDay: [false],
    });

    this.formGroup.get('entryType').valueChanges.subscribe((value: EntryType) => {
      this.entryTypeValue.set(value ?? null);
      this.applyEntryTypeRules(this.entryTypeValue());
    });
    this.formGroup.get('dateIn').valueChanges.subscribe((value: string | null) => {
      this.syncDateOutDateWithDateIn(value);
    });
    this.entryTypeValue.set((this.formGroup.get('entryType').value as EntryType) ?? null);
    this.applyEntryTypeRules(this.entryTypeValue());

    this.storageService.get<Options>(storageKeys.options).then((options) => {
      this.milageAllowance = options?.milageAllowance ?? 0;
      this.updateMilageAllowanceSummary(this.formGroup.get('kmPayUnit').value);
    });
  }

  private toDateTimeLocal(value: Date): string {
    return format(value, 'yyyy-MM-dd\'T\'HH:mm');
  }

  private syncDateOutDateWithDateIn(dateInValue: string | null) {
    if (!dateInValue) {
      return;
    }
    const currentDateOutValue = this.formGroup.get('dateOut').value as string | null;
    if (!currentDateOutValue) {
      return;
    }

    const nextDateIn = new Date(dateInValue);
    const currentDateOut = new Date(currentDateOutValue);
    if (Number.isNaN(nextDateIn.getTime()) || Number.isNaN(currentDateOut.getTime())) {
      return;
    }

    currentDateOut.setFullYear(nextDateIn.getFullYear(), nextDateIn.getMonth(), nextDateIn.getDate());
    this.formGroup.get('dateOut').setValue(this.toDateTimeLocal(currentDateOut), { emitEvent: false });
  }

  private setProject(project: SiteProject) {
    this.formGroup.get('project').setValue(project.guid);
    this.formGroup.get('projectName').setValue(project.name);
    this.selectedProjectForPicker.set(project);
  }

  private applyEntryTypeRules(value: EntryType) {
    const site = this.formGroup.get('site');
    const absence = this.formGroup.get('absence');
    const project = this.formGroup.get('project');
    if (value === 'work') {
      site.setValidators([Validators.required]);
      absence.clearValidators();
      if (this.isManagerWorkerRole()) {
        project.clearValidators();
      } else {
        project.setValidators([Validators.required]);
      }
      if (project.value === absenceSite) {
        project.setValue(null);
        this.formGroup.get('projectName').setValue(null);
      }
      this.formGroup.get('absence').setValue(null);
    } else if (value === 'absence') {
      site.clearValidators();
      absence.setValidators([Validators.required]);
      project.clearValidators();
      this.formGroup.get('site').setValue(absenceSite);
      this.formGroup.get('siteName').setValue(absenceSite);
      this.formGroup.get('project').setValue(absenceSite);
      this.formGroup.get('projectName').setValue(absenceSite);
      this.formGroup.get('littera').setValue(null);
      this.formGroup.get('kmPayUnit').setValue('');
      this.formGroup.get('fullDay').setValue(false);
      this.formGroup.get('partDay').setValue(false);
      this.updateMilageAllowanceSummary('');
    } else {
      site.clearValidators();
      absence.clearValidators();
      project.clearValidators();
    }
    site.updateValueAndValidity();
    absence.updateValueAndValidity();
    project.updateValueAndValidity();
  }

  selectEntryType(type: EntryType) {
    this.formGroup.get('entryType').setValue(type);
  }

  onUserByGuid(userGuid: string | null | undefined, users: User[]) {
    if (!userGuid) {
      return;
    }
    const selectedUser = users.find((user) => user.guid === userGuid);
    if (!selectedUser) {
      return;
    }
    const normalizedUser: SelectedUser = {
      guid: selectedUser.guid,
      displayName: selectedUser.displayName,
      name: selectedUser.displayName,
      role: selectedUser.role,
      worksites: selectedUser.worksites,
    };
    this.formGroup.get('user').setValue(normalizedUser.guid);
    this.formGroup.get('username').setValue(normalizedUser.displayName ?? normalizedUser.name);
    this.user$.next(normalizedUser);
    if (this.site() == null) {
      this.formGroup.get('site').setValue('');
      this.formGroup.get('siteName').setValue(null);
      this.site$.next(null);
      this.selectedSiteForPicker.set(null);
      this.selectedProjectForPicker.set(null);
    }
  }

  onSiteSelected(selectedSite: Site | null) {
    this.formGroup.get('site').setValue(selectedSite?.guid ?? null);
    this.formGroup.get('siteName').setValue(selectedSite?.name ?? null);
    this.formGroup.get('project').setValue(null);
    this.formGroup.get('projectName').setValue(null);
    this.formGroup.get('littera').setValue(null);
    this.site$.next(selectedSite);
    this.selectedSiteForPicker.set(selectedSite);
    this.selectedProjectForPicker.set(null);
  }

  onProjectSelected(selectedProject: SiteProject | null) {
    if (!selectedProject) {
      this.formGroup.get('project').setValue(null);
      this.formGroup.get('projectName').setValue(null);
      this.selectedProjectForPicker.set(null);
      return;
    }
    this.setProject(selectedProject);
    this.selectedProjectForPicker.set(selectedProject);
  }

  onMilageAllowanceInput(value: string) {
    this.formGroup.get('kmPayUnit').setValue(value);
    this.updateMilageAllowanceSummary(value);
  }

  private updateMilageAllowanceSummary(value: string) {
    const km = Number(value);
    if (!Number.isFinite(km) || km <= 0) {
      this.milageAllowanceSummary = '0.00';
      return;
    }
    this.milageAllowanceSummary = (km * (this.milageAllowance ?? 0)).toFixed(2);
  }

  getAbsenceOptions() {
    return this.absences;
  }

  private buildPaymentTypes(): TimesheetPaymentType[] {
    const paymentTypes: TimesheetPaymentType[] = [];
    const kmPayUnit = this.formGroup.get('kmPayUnit').value;
    const kmValue = Number(kmPayUnit);
    if (Number.isFinite(kmValue) && kmValue > 0) {
      paymentTypes.push({ paymentType: 'kmPay', paymentTypeUnit: String(kmValue), paymentTypeText: '' });
    }
    if (this.formGroup.get('fullDay').value) {
      paymentTypes.push({ paymentType: 'fullDay', paymentTypeUnit: '', paymentTypeText: '' });
    }
    if (this.formGroup.get('partDay').value) {
      paymentTypes.push({ paymentType: 'partDay', paymentTypeUnit: '', paymentTypeText: '' });
    }
    return paymentTypes;
  }

  onSave() {
    if (this.entryTypeValue() === 'work' && !this.isManagerWorkerRole() && !this.formGroup.get('project').value) {
      this.formGroup.get('project').markAsTouched();
      this.formGroup.markAllAsTouched();
      return;
    }
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValue = this.formGroup.value;
    const dateIn = new Date(formValue.dateIn);
    const dateOut = new Date(formValue.dateOut);
    const entryType = formValue.entryType as EntryType;

    const timesheet: Timesheet = {
      date: format(dateIn, 'dd.MM.yyyy'),
      dateIn,
      dateOut,
      checked: false,
      contractor: formValue.contractor,
      project: entryType === 'absence' ? absenceSite : formValue.project ?? '',
      projectName: entryType === 'absence' ? absenceSite : formValue.projectName ?? '',
      site: entryType === 'absence' ? absenceSite : formValue.site,
      siteName: entryType === 'absence' ? absenceSite : formValue.siteName,
      user: formValue.user,
      username: formValue.username,
      absence: entryType === 'absence' ? formValue.absence : null,
      littera: entryType === 'work' ? formValue.littera ?? null : null,
      paymentTypes: entryType === 'work' ? this.buildPaymentTypes() : [],
      status: 'active',
    };

    this.dismiss(timesheet);
  }

  dismiss(timesheet?: Timesheet) {
    this.modalCtrl.dismiss(timesheet);
  }
}
